import React, { useState, useEffect } from 'react';

import { FormInput_text, Datepicker, Button, formInput_funcUtility } from "components";

import { 
    func_validation, 
    func_currency,
    getObjectPropertyValue_stringTrimNotEmpty, 
    getObjectPropertyValue_validNumber, 
    isArrayNotEmpty, 
    isDateValid, 
    isFunction,
    isStringTrimNotEmpty,
    isNumberOrStringValid,
    getRegexMatchCurrencyDoublePrecisionCaseInsensitive,
    getParsedFormattedFloat
} from "functions";

import ic_small_add from "assets/images/ic_small_add.svg";
import ic_small_close from "assets/images/ic_small_close.svg";
import moment from 'moment';



export function useBillingSetupDiscounts(props) {
    const { 
        isEditing,
        billingSetup,
        isFormSubmissionAttempted,
        isDraft, // Get draft status to control input disabling
        billingSetupId, // Parent billingSetupId - empty string when cloning
        billingSetupDiscountId // Discount ID - empty string when cloning
    } = props;

    // Initialize discounts from existing billing setup
    let discounts_init = [];
    if (billingSetup?.BillingSetupDiscounts) {
        discounts_init = billingSetup.BillingSetupDiscounts.map(discount => ({
            // When cloning, reset IDs to 0 (similar to BillingSetup and BillingSetupDetails pattern)
            id: (billingSetupDiscountId === "" || !isEditing) ? 0 : (discount.Id || 0),
            billingSetupId: (billingSetupId === "" || !isEditing) ? 0 : (discount.BillingSetupId || 0),
            unitDiscountAmount: func_currency.validateStringDoublePrecisionCurrency(
                (discount.UnitDiscountAmount || 0) + "", 
                '0.00'
            ),
            discountStartDateTime: getObjectPropertyValue_stringTrimNotEmpty(
                discount, 
                "DiscountStartDateTime", 
                (discount.Id && discount.Id > 0)? null : moment().format("YYYY-MM-DD")
            ),
            discountEndDateTime: getObjectPropertyValue_stringTrimNotEmpty(
                discount, 
                "DiscountEndDateTime", 
                (discount.Id && discount.Id > 0)? null : moment().add(1, 'year').format("YYYY-MM-DD")
            ),
            isDeleted: discount.IsDeleted || false,
            // Track if this is from database - false when cloning
            isFromDatabase: (discount.Id && discount.Id > 0 && isEditing && billingSetupDiscountId !== "")
        }));
    }

    // States
    const [discounts, setDiscounts] = useState(discounts_init);
    const [isAnyInputChanged, setIsAnyInputChanged] = useState(false);
    const [editingEndDateIndex, setEditingEndDateIndex] = useState(null); // Track which discount's end date is being edited

    // Check for changes in input from initial
    useEffect(() => {
        let isNotChangedFromOriginal = JSON.stringify(discounts) === JSON.stringify(discounts_init);
        let isChangedFromEmpty = discounts.length > 0 && discounts.some(d => 
            d.unitDiscountAmount || d.discountStartDateTime || d.discountEndDateTime
        );

        setIsAnyInputChanged(isEditing ? !isNotChangedFromOriginal : isChangedFromEmpty);

    }, [discounts]);

    // Add a new discount entry
    function handleAddDiscount() {
        const newDiscount = {
            id: 0,
            billingSetupId: 0,
            unitDiscountAmount: "0.00",
            discountStartDateTime: moment().format("YYYY-MM-DD"),
            discountEndDateTime: moment().add(1, 'year').format("YYYY-MM-DD"),
            isDeleted: false,
            isFromDatabase: false
        };
        setDiscounts([...discounts, newDiscount]);
    }

    // Remove a discount entry by index
    function handleRemoveDiscount(index) {
        const updatedDiscounts = discounts.filter((_, idx) => idx !== index);
        setDiscounts(updatedDiscounts);
    }

    // Update a specific discount field
    function updateDiscountField(index, field, value) {
        const updatedDiscounts = [...discounts];
        updatedDiscounts[index] = {
            ...updatedDiscounts[index],
            [field]: value
        };
        setDiscounts(updatedDiscounts);
    }

    // Validate all discounts (skip deleted ones)
    function validateDiscounts() {
        if (!isArrayNotEmpty(discounts)) return true;

        return discounts.every(discount => {
            // Skip validation for deleted discounts
            if (discount.isDeleted) return true;
            
            const hasValidAmount = isNumberOrStringValid(discount.unitDiscountAmount);
            const hasValidStartDate = !discount.discountStartDateTime || isDateValid(discount.discountStartDateTime);
            const hasValidEndDate = !discount.discountEndDateTime || isDateValid(discount.discountEndDateTime);
            
            // Check if end date is after start date
            const isDateRangeValid = (discount.discountStartDateTime && discount.discountEndDateTime) ?
                moment(discount.discountEndDateTime).isAfter(moment(discount.discountStartDateTime)) : 
                true;

            return hasValidAmount && hasValidStartDate && hasValidEndDate && isDateRangeValid;
        });
    }

    // Display component
    const display = (
        <div className="recursiveBillingSetupAccountUpdate-billingSetupDiscounts">
            <div className="billingSetupDiscounts-header d-flex justify-content-between align-items-center">
                <span className="billingSetupDiscounts-title">Billing Setup Discounts</span>
                <Button
                    className="billingSetupDiscounts-addButton"
                    handleOnClick={handleAddDiscount}
                    isDecoration_transparentDarkGreyBorder
                >
                    <img src={ic_small_add} alt="Add Discount" />
                    <span>Add Discount</span>
                </Button>
            </div>

            {discounts.map((discount, index) => {
                const isDiscountFromDb = discount.isFromDatabase;

                const isDiscountDeleted = discount.isDeleted;
                // Check if discount has expired (end date is before today)
                // Only check expiration if user is NOT currently editing this discount's end date
                const isDiscountExpired = !isDraft && 
                                          editingEndDateIndex !== index && 
                                          discount.discountEndDateTime && (moment(discount.discountEndDateTime).add(1, 'month')).isBefore(moment(), 'day');

                console.log("expired date: ", moment(discount.discountEndDateTime).add(1, 'month'));
                console.log("(moment(discount.discountEndDateTime).add(1, 'month')).isBefore(moment(), 'day'): ", (moment(discount.discountEndDateTime).add(1, 'month')).isBefore(moment(), 'day'));

                // Combine deleted and expired status - both should disable editing
                const isDiscountDisabled = isDiscountDeleted || isDiscountExpired;
                const itemClassName = `billingSetupDiscount-item d-flex flex-wrap align-items-end${isDiscountDisabled ? ' billingSetupDiscount-item--deleted' : ''}`;
                
                return (
                <div key={index} className={itemClassName}>
                    {/* Unit Discount Amount */}
                    <FormInput_text
                        inputLabel={`Discount Amount ${index + 1} (RM)`}
                        
                        className="billingSetupDiscount-unitDiscountAmount"
                        customInputClassName="billingSetupDiscount-unitDiscountAmount-input"
                        inputValue={discount.unitDiscountAmount}

                        handleInputBlurExtend={(value) => {
                            const validatedValue = isStringTrimNotEmpty(value) ? value : "0.00";
                            updateDiscountField(index, 'unitDiscountAmount', validatedValue);
                        }}

                        inputMaxLength={64}

                        customErrorMsgDefault="Required"
                        isFormSubmissionAttempted={isFormSubmissionAttempted}

                        handleInputFocusExtend={(e) => { 
                            formInput_funcUtility.handleInputFocus_onEnter_selectInput(e); 
                        }}
                        isValuePropagationGuarded_onChange={(value) => { 
                            return (value.match(getRegexMatchCurrencyDoublePrecisionCaseInsensitive()) || value === '') 
                        }}
                        customValidator_onBlur={(value) => { 
                            return func_currency.validateStringDoublePrecisionCurrency(value, '0.00'); 
                        }}

                        isInputValueTrim_onChange
                        isInputBlurHandlingRequired 
                        isInputValueTrim_onBlur
                        isMarginBtmOptional

                        isInputDisabled={isDiscountDisabled} // {isDiscountDisabled || (isDiscountFromDb && !isDraft)}
                        inputCustomOptions={isDiscountDisabled ? { style: { cursor: 'not-allowed' } } : {}}
                    />

                    {/* Discount Start DateTime */}
                    <Datepicker
                        className="billingSetupDiscount-startDate"
                        inputClassName="billingSetupDiscount-startDate-input"
                        
                        label={`Start Date ${index + 1}`}
                        dateFromParent={discount.discountStartDateTime}
                        handleOnChange={(value) => updateDiscountField(index, 'discountStartDateTime', value)}
                        handleOnEmptyInput={() => updateDiscountField(index, 'discountStartDateTime', null)}

                        isFormSubmissionAttempted={isFormSubmissionAttempted}
                        errorMsg="Empty or Date only"

                        isConversionRequired_ISO8601 
                        // isInputRequired

                        isDisabled={isDiscountDisabled} // {isDiscountDisabled || (isDiscountFromDb && !isDraft)} // Enable database fields when draft is true
                        otherOptions={{
                            ...(isDiscountDisabled ? { style: { cursor: 'not-allowed' } } : {}),
                            isClearable: true, // !(isDiscountDisabled || (isDiscountFromDb && !isDraft))
                            clearButtonTitle: "Clear date"
                        }}
                    />

                    {/* Discount End DateTime */}
                    <Datepicker
                        className="billingSetupDiscount-endDate"
                        inputClassName="billingSetupDiscount-endDate-input"
                        
                        label={`End Date ${index + 1}`}
                        dateFromParent={discount.discountEndDateTime}
                        handleOnChange={(value) => updateDiscountField(index, 'discountEndDateTime', value)}
                        handleOnEmptyInput={() => updateDiscountField(index, 'discountEndDateTime', null)}

                        isFormSubmissionAttempted={isFormSubmissionAttempted}
                        errorMsg="Empty or Date only"

                        isConversionRequired_ISO8601 
                        // isInputRequired

                        isDisabled={isDiscountDisabled} // {isDiscountDisabled || (isDiscountFromDb && !isDraft)} // Enable database fields when draft is true
                        otherOptions={{
                            ...(isDiscountDisabled ? { style: { cursor: 'not-allowed' } } : {}),
                            isClearable: true, // !(isDiscountDisabled || (isDiscountFromDb && !isDraft))
                            clearButtonTitle: "Clear date",
                            onFocus: () => setEditingEndDateIndex(index),  // Mark as being edited when focused
                            onBlur: () => setEditingEndDateIndex(null)     // Mark as not being edited when blurred
                        }}
                    />

                    {/* Remove Button - Hidden for deleted/expired items, always shown for others (even if only one item) */}
                    {!isDiscountDisabled && (
                        <Button
                            className="billingSetupDiscount-removeButton"
                            handleOnClick={() => handleRemoveDiscount(index)}
                            isDecoration_transparentDarkGreyBorder
                        >
                            <img src={ic_small_close} alt="Remove Discount" />
                        </Button>
                    )}
                </div>
                );
            })}
        </div>
    );

    const isEveryInputValid = validateDiscounts();

    // Format discounts data for submission
    const billingSetupDiscountsDetails = discounts.map(discount => ({
        Id: discount.id,
        BillingSetupId: discount.billingSetupId,
        UnitDiscountAmount: getParsedFormattedFloat(discount.unitDiscountAmount),
        DiscountStartDateTime: discount.discountStartDateTime || null,
        DiscountEndDateTime: discount.discountEndDateTime || null,
        IsDeleted: discount.isDeleted
    }));

    return {
        billingSetupDiscountsDetails,
        
        isEveryInputValid,
        isAnyInputChanged,
        
        display
    };
}
